import React, { useEffect, useState } from "react";
import { BackButton, StudentAttendance, StudentResult, PhotoUploadModal } from "../components";
import { StudentAddons } from "../components";
import { useLocation } from "react-router-dom";
import { useStateContext } from "../contexts/ContextProvider";
import { userRequest } from "../utils/requestMethods";
import { FiCamera, FiUser, FiChevronDown, FiChevronUp, FiInfo, FiExternalLink } from "react-icons/fi";
import { toast } from "react-toastify";
import { isBypassActive } from "../utils/paymentThresholdUtils";

// Enrollment metadata is a free-form object — its keys depend entirely on
// whatever the school's own enrollment form asked for, so this can't be a
// fixed field list. IDs are internal bookkeeping (e.g. enrolment_submission_id)
// and never worth showing an admin.
const HIDDEN_METADATA_KEYS = new Set(["enrolment_submission_id"]);

const humanizeMetadataKey = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatMetadataValue = (key, value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (/date|dob/i.test(key)) {
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString("en-NG", { day: "2-digit", month: "long", year: "numeric" });
    }
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.map((v) => String(v)).join(", ") : "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

// Some enrollment forms collect uploaded documents (birth certificate,
// passport, ID, etc.) — those come through metadata as plain URL strings
// indistinguishable from any other field, so detect and render them as
// links/thumbnails instead of a raw dumped URL.
const getMetadataUrl = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : null;
};

const isImageUrl = (url) =>
  /\.(jpe?g|png|gif|webp|bmp|svg)(\?.*)?$/i.test(url) || url.includes("/image/upload/");

const StudentProfile = ({ history }) => {
  const location = useLocation();
  const [board, setBoard] = useState(location.state?.tab || "attendance");
  const studentId = location.pathname.split("/")[3];
  const student = location.state?.student;
  console.log("Student:", student);
  const classId = location.pathname.split("/")[4];
  // Graduated students (and any other route without a real class, e.g.
  // GraduatedStudents.jsx) link here with the "none" placeholder since the
  // route requires a :classId segment — treat that as "no class" rather
  // than sending it to the backend, which rejects it as an invalid class.
  const hasRealClass = Boolean(classId) && classId !== "none" && classId !== "null";
  const { user, school } = useStateContext();
  const schoolTerm = school?.settings?.current_term;
  const schoolSession = school?.settings?.current_session;
  const terminology = school?.settings?.term_terminology;
  const [studentAttendance, setStudentAttendance] = useState([]);
  const [loading, setLoading] = useState(false);

  // Photo upload states
  const [currentStudentPhoto, setCurrentStudentPhoto] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  // Login exemption / bypass threshold state
  const [bypassPaymentThreshold, setBypassPaymentThreshold] = useState(false);
  const [bypassLoading, setBypassLoading] = useState(false);

  // Additional details parents filled on the enrollment form, stored as a
  // free-form object on the child record — collapsed by default since a
  // school's own custom form can produce a lot of fields.
  const [enrollmentMetadata, setEnrollmentMetadata] = useState({});
  const [showEnrollmentDetails, setShowEnrollmentDetails] = useState(false);

  const [studentStats, setStudentStats] = useState({
    totalDays: 0,
    daysPresent: 0,
    daysAbsent: 0,
    attendanceRate: 0,
  });
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchAttendance = async (term = null, session = null) => {
    // No real class to scope this to (e.g. a graduated student) — the
    // backend rejects a class_id it can't resolve, so don't ask.
    if (!hasRealClass) {
      setStudentAttendance([]);
      return;
    }
    setLoading(true);
    try {
      const termToUse = term || schoolTerm || "1";
      const sessionToUse = session || schoolSession || "2024/2025";
      const { data } = await userRequest.get(
        `/attendance/student/${studentId}?class_id=${classId}&school_id=${school?._id}&term=${termToUse}&session=${sessionToUse}&limit=50`,
      );
      setStudentAttendance(data?.data || []);
    } catch (error) {
      console.error("Error fetching attendance:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentAttendanceStats = async () => {
    if (!school?._id || !studentId) return;
    setStatsLoading(true);
    try {
      const term = schoolTerm || "1";
      const session = schoolSession || "2024/2025";
      const { data } = await userRequest.get(
        `/attendance/stats?child_id=${studentId}&school_id=${school._id}&term=${term}&session=${session}`,
      );
      setStudentStats({
        totalDays: data.total_days ?? 0,
        daysPresent: data.total_present_days ?? 0,
        daysAbsent: data.total_absent_days ?? 0,
        attendanceRate: data.total_attendance_rate ?? 0,
      });
    } catch (error) {
      console.error("Error fetching student attendance stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchStudentDetails = async () => {
    try {
      const response = await userRequest.get(`/children/${studentId}`);
      const studentData = response.data;
      if (studentData?.picture?.path) {
        setCurrentStudentPhoto(studentData.picture.path);
      } else {
        setCurrentStudentPhoto(null);
      }
      setEnrollmentMetadata(studentData?.metadata || {});
      setBypassPaymentThreshold(
        isBypassActive(studentData?.bypass_payment_threshold, schoolTerm, schoolSession),
      );
    } catch (error) {
      console.error("Error fetching student details:", error);
    }
  };

  const handleBypassToggle = async () => {
    const newValue = !bypassPaymentThreshold;
    setBypassLoading(true);
    try {
      const payload = {
        student_ids: [studentId],
        enabled: newValue,
        term: schoolTerm ? Number(schoolTerm) : undefined,
        session: schoolSession,
      };
      await userRequest.post(
        `/children/schools/${school?._id}/bulk-bypass-payment-threshold`,
        payload,
      );
      setBypassPaymentThreshold(newValue);
      toast.success(
        newValue
          ? `Login exemption granted for Term ${schoolTerm} (${schoolSession}).`
          : "Login exemption removed: School payment threshold is now enforced.",
      );
    } catch (error) {
      toast.error("Failed to update student login exemption");
    } finally {
      setBypassLoading(false);
    }
  };

  const metadataEntries = Object.entries(enrollmentMetadata || {}).filter(
    ([key, value]) =>
      !HIDDEN_METADATA_KEYS.has(key) &&
      !key.endsWith("_id") &&
      value !== undefined &&
      value !== null &&
      value !== "",
  );

  const handleOpenPhotoModal = () => {
    setShowPhotoModal(true);
  };

  const handleUploadSuccess = () => {
    // Refresh the student photo after successful upload
    fetchStudentDetails();
  };

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      if (isMounted && school?._id && studentId) {
        await Promise.all([fetchStudentDetails(), fetchStudentAttendanceStats()]);
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [school?._id, studentId]);

  return (
    <div className="w-full flex items-center justify-center py-14 md:py-8 relative">
      <div className="bg-transparent w-full pb-16 px-9 py-8 sm:py-0">
        <div className="bg-white rounded-2xl p-6">
          <BackButton history={history} />

          {/* Student Photo and Exemption Section */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 mt-4">
            <div className="flex items-center">
              <div className="relative">
                <div className="w-14 h-14 rounded-full bg-blue-500 border-[3px] border-[#D8846E] overflow-hidden">
                  {currentStudentPhoto ? (
                    <img
                      src={currentStudentPhoto}
                      alt="Student"
                      className="w-full h-full rounded-full object-cover"
                      onError={(e) => {
                        e.target.style.display = "none";
                        setCurrentStudentPhoto(null);
                      }}
                    />
                  ) : (
                    <div className="w-full h-full rounded-full bg-blue-500 flex items-center justify-center">
                      <FiUser className="w-8 h-8 text-white" />
                    </div>
                  )}
                </div>
                <button
                  onClick={handleOpenPhotoModal}
                  className="absolute -bottom-1 -right-1 bg-[#3F3DA5] hover:bg-blue-800 text-white rounded-full p-1.5 border-2 border-white shadow-lg transition-colors"
                  title="Upload Photo"
                >
                  <FiCamera className="w-3 h-3" />
                </button>
              </div>

              <div className="ml-4">
                <p className="text-2xl font-semibold mb-1">{student}</p>
                <p className="text-sm text-[#606060]">Student Profile</p>
              </div>
            </div>

            {/* Login Exemption Control */}
            <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5">
              <div>
                <p className="text-xs font-semibold text-slate-700">CBT Login Exemption</p>
                <p className="text-[11px] text-slate-500">
                  {bypassPaymentThreshold
                    ? `Exempted for Term ${schoolTerm || 1} (${schoolSession || "Current"})`
                    : `Threshold enforced (${school?.settings?.result?.parent_payment_threshold || 0}%)`}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={bypassPaymentThreshold}
                  onChange={handleBypassToggle}
                  disabled={bypassLoading}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#3F3DA5]"></div>
              </label>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between">
            <div className="w-full sm:w-max mb-4 sm:mb-0">
              <p className="text-sm text-[#606060]">
                {schoolTerm} {terminology ? terminology : "Term"} - {schoolSession} Attendance
              </p>
            </div>
            <button className="hover:bg-indigo-900 rounded-full text-sm text-white px-3 py-2 bg-[#3F3DA5]">
              Export Attendance
            </button>
          </div>

          <div className="border-t border-t-[#E7E7E7] mt-6 mb-6"></div>

          <div className="bg-[#EEEEFC] rounded-xl flex flex-wrap items-center justify-between">
            <div className="p-4">
              <p className="text-sm text-[#606060] mb-2">Total Days</p>
              <p className="text-3xl font-semibold">
                {statsLoading ? "—" : studentStats.totalDays}
              </p>
            </div>
            <div className="p-4">
              <p className="text-sm text-[#606060] mb-2">Days Present</p>
              <p className="text-3xl font-semibold">
                {statsLoading ? "—" : studentStats.daysPresent}
              </p>
            </div>
            <div className="p-4">
              <p className="text-sm text-[#606060] mb-2">Days Absent</p>
              <p className="text-3xl font-semibold">
                {statsLoading ? "—" : studentStats.daysAbsent}
              </p>
            </div>
            <div className="p-4">
              <p className="text-sm text-[#606060] mb-2">Attendance Rate</p>
              <p className="text-3xl font-semibold">
                {statsLoading ? "—" : `${studentStats.attendanceRate}%`}
              </p>
            </div>
          </div>
        </div>

        {metadataEntries.length > 0 && (
          <div className="bg-white rounded-2xl mt-6 px-6 py-6">
            <button
              type="button"
              onClick={() => setShowEnrollmentDetails((v) => !v)}
              className="w-full flex items-center justify-between gap-3"
            >
              <div className="flex items-center gap-2.5">
                <FiInfo className="text-[#3F3DA5] w-4 h-4 shrink-0" />
                <span className="text-sm font-semibold text-gray-800">
                  Additional Enrollment Details
                </span>
                <span className="text-xs font-medium text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">
                  {metadataEntries.length}
                </span>
              </div>
              {showEnrollmentDetails ? (
                <FiChevronUp className="text-gray-400 w-4 h-4 shrink-0" />
              ) : (
                <FiChevronDown className="text-gray-400 w-4 h-4 shrink-0" />
              )}
            </button>

            {showEnrollmentDetails && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4 mt-5">
                {metadataEntries.map(([key, value]) => {
                  const url = getMetadataUrl(value);
                  return (
                    <div key={key} className="min-w-0">
                      <p className="text-xs text-gray-400 mb-0.5">{humanizeMetadataKey(key)}</p>
                      {url ? (
                        isImageUrl(url) ? (
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block"
                            title={humanizeMetadataKey(key)}
                          >
                            <img
                              src={url}
                              alt={humanizeMetadataKey(key)}
                              className="w-16 h-16 rounded-lg object-cover border border-gray-200 hover:opacity-80 transition-opacity"
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          </a>
                        ) : (
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700"
                          >
                            View file <FiExternalLink className="w-3 h-3" />
                          </a>
                        )
                      ) : (
                        <p className="text-sm font-medium text-gray-800 break-words">
                          {formatMetadataValue(key, value)}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="bg-white rounded-2xl mt-6 px-6 py-7">
          <div className="flex flex-wrap items-center my-3">
            <button
              onClick={() => {
                setBoard("attendance");
              }}
              className={
                board === "attendance"
                  ? "sm:w-max w-full bg-[#C5C5E8] hover:bg-indigo-300 border border-[#C5C5E8] text-[#3F3DA5] mr-4 font-semibold text-sm px-4 py-2 rounded-full items-center justify-between"
                  : "sm:w-max w-full bg-white hover:bg-indigo-300 border border-[#C5C5E8] text-[#3F3DA5] mr-4 font-semibold text-sm px-4 py-2 rounded-full items-center justify-between"
              }
            >
              Attendance
            </button>

            {/*<button onClick={() => { setBoard("fees") }} className={board === "fees" ? "sm:w-max w-full bg-[#C5C5E8] hover:bg-indigo-300 border border-[#C5C5E8] text-[#3F3DA5] mr-4 font-semibold text-sm px-4 py-2 rounded-full items-center justify-between"
                            : "sm:w-max w-full bg-white hover:bg-indigo-300 border border-[#C5C5E8] text-[#3F3DA5] mr-4 font-semibold text-sm px-4 py-2 rounded-full items-center justify-between"
                        }>
                            Extra Fees
                        </button>*/}

            <button
              onClick={() => {
                setBoard("result");
              }}
              className={
                board === "result"
                  ? "sm:w-max w-full bg-[#C5C5E8] hover:bg-indigo-300 border border-[#C5C5E8] text-[#3F3DA5] mr-4 font-semibold text-sm px-4 py-2 rounded-full items-center justify-between"
                  : "sm:w-max w-full bg-white hover:bg-indigo-300 border border-[#C5C5E8] text-[#3F3DA5] mr-4 font-semibold text-sm px-4 py-2 rounded-full items-center justify-between"
              }
            >
              Result
            </button>
          </div>
          {!hasRealClass && board === "attendance" && (
            <div className="mb-4 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-700">
              This student has no current class (e.g. they've graduated), so attendance can't be scoped to one. See the Result tab for their academic history instead.
            </div>
          )}
          {board === "attendance" ? (
            <StudentAttendance
              studentAttendance={studentAttendance}
              loading={loading}
              onAttendanceView={fetchAttendance}
              school={school}
              studentId={studentId}
              classId={classId}
              studentName={student}
            />
          ) : board === "fees" ? (
            <StudentAddons />
          ) : (
            <StudentResult
              school={school}
              terminology={terminology}
              studentId={studentId}
              classId={classId}
              attendanceData={studentStats}
              selectedTerm={schoolTerm}
              selectedSession={schoolSession}
            />
          )}
        </div>
      </div>

      {/* Photo Upload Modal */}
      <PhotoUploadModal
        isOpen={showPhotoModal}
        onClose={() => setShowPhotoModal(false)}
        onUploadSuccess={handleUploadSuccess}
        uploadEndpoint={`/children/${school?._id}/upload-photo/${studentId}`}
        title="Upload Student Photo"
        maxFileSize={1024 * 1024} // 1MB
      />
    </div>
  );
};
export default StudentProfile;
